import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/lib/data";

const Testimonials = () => {
    const [idx, setIdx] = useState(0);
    const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);
    const prev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
    const t = TESTIMONIALS[idx];

    return (
        <section data-testid="testimonials-section" className="sxe-section relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="hero-orb w-[500px] h-[500px] bg-[#A100FF]/12 top-1/2 -translate-y-1/2 -left-32" />
            </div>
            <div className="sxe-container relative">
                <div className="max-w-3xl">
                    <div className="overline"><span className="dot" /> Customer Voices</div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05] text-white mt-5">
                        Enterprises are <span className="text-gradient">building faster</span>.
                    </h2>
                </div>

                <div className="mt-14 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-stretch">
                    <div className="sxe-card p-8 md:p-10 relative overflow-hidden min-h-[280px]">
                        <Quote className="absolute right-8 top-8 w-16 h-16 text-[#A100FF]/15" />
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -12 }}
                                transition={{ duration: 0.35 }}
                            >
                                <p className="font-display text-white text-xl md:text-2xl leading-relaxed tracking-tight max-w-2xl">
                                    &ldquo;{t.quote}&rdquo;
                                </p>
                                <div className="mt-8 flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center font-display text-white font-medium">
                                        {t.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="font-display text-white font-medium tracking-tight">{t.name}</div>
                                        <div className="text-[#B0A3C4] text-sm">{t.role}</div>
                                    </div>
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        <div className="absolute bottom-6 right-6 flex items-center gap-2">
                            <button
                                onClick={prev}
                                data-testid="testimonial-prev"
                                aria-label="Previous"
                                className="w-9 h-9 rounded-full border border-white/10 text-white hover:border-[#C266FF]/50 hover:bg-[#A100FF]/10 transition-colors flex items-center justify-center"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                                onClick={next}
                                data-testid="testimonial-next"
                                aria-label="Next"
                                className="w-9 h-9 rounded-full border border-white/10 text-white hover:border-[#C266FF]/50 hover:bg-[#A100FF]/10 transition-colors flex items-center justify-center"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {TESTIMONIALS.map((tst, i) => (
                            <button
                                key={tst.name}
                                onClick={() => setIdx(i)}
                                data-testid={`testimonial-thumb-${i}`}
                                className={`text-left rounded-2xl p-5 border transition-all ${
                                    i === idx
                                        ? "border-[#C266FF]/50 bg-[#A100FF]/10"
                                        : "border-white/[0.06] bg-white/[0.02] hover:border-white/20"
                                }`}
                            >
                                <div className="font-display text-white text-sm font-medium tracking-tight">{tst.name}</div>
                                <div className="text-[#B0A3C4] text-[12px] mt-1 line-clamp-2">{tst.role}</div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
